import React from 'react';
import { Link } from 'react-router-dom';
import { MessageSquare } from 'lucide-react';
import PublicFooter from '../components/PublicFooter';

const Privacy = () => {
  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <header className="border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Link to="/" className="flex items-center space-x-2">
            <MessageSquare className="h-8 w-8 text-blue-600" />
            <span className="text-2xl font-bold text-gray-900">ChatFlow</span>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Privacy Policy — ChatFlow</h1>
          <p className="text-gray-500 mb-8">Last updated: 2026-02-02</p>

          <div className="prose prose-gray max-w-none">
            <p className="text-lg text-gray-700 mb-8">
              ChatFlow ("ChatFlow", "we", "us", "our") provides a platform to automate customer conversations and manage messaging workflows. This Privacy Policy explains how we collect, use, share, and protect personal information when you use our website and services (the "Service").
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-10 mb-4">1. Who We Are</h2>
            <p className="text-gray-700 mb-6">
              ChatFlow is the data controller for personal information processed through the Service. If you have questions about this policy or your data, contact us at <a href="mailto:contact@chatflow.info" className="text-blue-600 hover:underline">contact@chatflow.info</a>.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-10 mb-4">2. Information We Collect</h2>
            <p className="text-gray-700 mb-4">We may collect the following categories of information:</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-6">
              <li><strong>Account information:</strong> name, email address, password (stored securely/hashed), organization details (if provided).</li>
              <li><strong>Authentication data:</strong> login/session tokens and related technical identifiers.</li>
              <li><strong>Integrations data:</strong> when you connect third-party services (e.g., Meta platforms such as Facebook/Instagram), we may receive identifiers (such as user or page IDs), access tokens/permissions, and related metadata required to provide the integration.</li>
              <li><strong>Usage data:</strong> actions you take in the Service, feature usage, logs, and settings you configure.</li>
              <li><strong>Device and technical data:</strong> IP address, browser type, device identifiers, operating system, timestamps, error logs.</li>
              <li><strong>Communications:</strong> messages you send us (support requests, emails).</li>
            </ul>

            <h2 className="text-2xl font-semibold text-gray-900 mt-10 mb-4">3. How We Use Information</h2>
            <p className="text-gray-700 mb-4">We use information to:</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-6">
              <li>Provide, operate, and maintain the Service.</li>
              <li>Create and manage your account, authenticate users, and prevent fraud.</li>
              <li>Enable integrations you request and perform requested actions through connected services.</li>
              <li>Improve features, performance, and user experience (including debugging and analytics).</li>
              <li>Communicate with you about updates, security notices, and support.</li>
              <li>Comply with legal obligations and enforce our Terms of Service.</li>
            </ul>

            <h2 className="text-2xl font-semibold text-gray-900 mt-10 mb-4">4. Legal Bases (UK/EU)</h2>
            <p className="text-gray-700 mb-4">Where UK/EU GDPR applies, we rely on one or more legal bases:</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-6">
              <li><strong>Contract:</strong> to provide the Service you request.</li>
              <li><strong>Legitimate interests:</strong> to secure and improve the Service and prevent abuse.</li>
              <li><strong>Consent:</strong> where required (e.g., certain cookies or optional marketing).</li>
              <li><strong>Legal obligation:</strong> to comply with applicable law.</li>
            </ul>

            <h2 className="text-2xl font-semibold text-gray-900 mt-10 mb-4">5. Sharing and Disclosure</h2>
            <p className="text-gray-700 mb-4">We do not sell your personal information. We may share information with:</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-6">
              <li><strong>Service providers (processors):</strong> hosting, databases, analytics, email delivery, error monitoring, customer support tooling—only as needed to run the Service.</li>
              <li><strong>Integration partners:</strong> when you connect third-party services (e.g., Meta platforms), data may be exchanged with those services to perform actions you request.</li>
              <li><strong>Legal and safety:</strong> if required to comply with law, protect rights and safety, or investigate misuse.</li>
            </ul>

            <h2 className="text-2xl font-semibold text-gray-900 mt-10 mb-4">6. International Transfers</h2>
            <p className="text-gray-700 mb-6">
              If information is transferred outside the UK/EU, we use appropriate safeguards (such as standard contractual clauses) where required.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-10 mb-4">7. Data Retention</h2>
            <p className="text-gray-700 mb-6">
              We keep personal information only as long as needed for the purposes described above, including to meet legal, accounting, or reporting requirements. You may request deletion as described below.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-10 mb-4">8. Security</h2>
            <p className="text-gray-700 mb-6">
              We use reasonable technical and organizational measures to protect information (e.g., encryption in transit, access controls). No method of transmission or storage is 100% secure.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-10 mb-4">9. Your Rights</h2>
            <p className="text-gray-700 mb-4">Depending on your location, you may have rights to:</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
              <li>Access, correct, or delete your personal information.</li>
              <li>Object to or restrict certain processing.</li>
              <li>Receive a copy of your data (data portability).</li>
              <li>Withdraw consent where processing is based on consent.</li>
            </ul>
            <p className="text-gray-700 mb-6">
              To exercise rights, contact <a href="mailto:contact@chatflow.info" className="text-blue-600 hover:underline">contact@chatflow.info</a>.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-10 mb-4">10. Cookies and Similar Technologies</h2>
            <p className="text-gray-700 mb-6">
              We may use cookies/local storage for login sessions, security, and basic analytics. You can control cookies through your browser settings. Some features may not work without essential cookies.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-10 mb-4">11. Children's Privacy</h2>
            <p className="text-gray-700 mb-6">
              The Service is not directed to children under 13 (or the minimum age required in your jurisdiction). We do not knowingly collect information from children.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-10 mb-4">12. Changes to This Policy</h2>
            <p className="text-gray-700 mb-6">
              We may update this policy from time to time. We will post the updated version on this page and update the "Last updated" date.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-10 mb-4">Contact</h2>
            <p className="text-gray-700 mb-6">
              If you have any questions, email <a href="mailto:contact@chatflow.info" className="text-blue-600 hover:underline">contact@chatflow.info</a>.
            </p>
          </div>
        </div>
      </main>

      <PublicFooter />
    </div>
  );
};

export default Privacy;
